import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { createAccountHead, getAccountHeadDetail, updateAccountHead, getAcGroups } from '../api/acHeadApi'

const INITIAL_FORM = {
  name: '',
  group: '',
  alias: '',
  place: '',
  cost_center: 0,
  budget: 0,
  amount: 0,
  balance_type: 'Dr', // 'Dr' | 'Cr'
  hsn_sac: '',
  agent: '',
  remarks: '',
  companies: [],
  branches: [],
  departments: [],
  status: 'Active',
}

export function useAcHeadEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const groupParam = searchParams.get('group')
  const isEdit = Boolean(id)

  const [form, setForm] = useState(INITIAL_FORM)
  const [availableGroups, setAvailableGroups] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    let active = true

    async function init() {
      setIsLoading(true)
      setError('')

      let groupsList = []
      try {
        const groupsData = await getAcGroups()
        if (Array.isArray(groupsData)) {
          groupsList = groupsData.map((g) => g.Name || g.name).filter(Boolean)
        }
      } catch (err) {
        console.warn('Failed to fetch account groups from API:', err)
      }

      if (active) {
        setAvailableGroups(groupsList)
        if (!isEdit && groupsList.length > 0) {
          const matchedGroup = groupParam
            ? groupsList.find(
                (g) =>
                  g.toLowerCase() === groupParam.toLowerCase() ||
                  g.toLowerCase().startsWith(groupParam.toLowerCase()) ||
                  groupParam.toLowerCase().startsWith(g.toLowerCase()),
              )
            : null
          setForm((prev) => ({ ...prev, group: matchedGroup || groupParam || prev.group || groupsList[0] }))
        }
      }


      if (isEdit) {
        try {
          const detail = await getAccountHeadDetail(id)
          if (active && detail) {
            setForm({
              name: detail.name || '',
              group: detail.group || '',
              alias: detail.alias || '',
              place: detail.place || '',
              cost_center: detail.cost_center ? 1 : 0,
              budget: Number(detail.budget || 0),
              amount: Number(detail.amount || 0),
              balance_type: detail.balance_type || 'Dr',
              hsn_sac: detail.hsn_sac || '',
              agent: detail.agent || '',
              remarks: detail.remarks || '',
              companies: Array.isArray(detail.companies) ? detail.companies : [],
              branches: Array.isArray(detail.branches) ? detail.branches : [],
              departments: Array.isArray(detail.departments) ? detail.departments : [],
              status: detail.status || 'Active',
            })
          }
        } catch (err) {
          if (active) setError(err?.message || 'Failed to load Account Head details')
        }
      }

      if (active) setIsLoading(false)
    }

    init()
    return () => {
      active = false
    }
  }, [id, isEdit])

  function patchForm(field, value) {
    if (typeof field === 'object' && field !== null) {
      setForm((prev) => ({ ...prev, ...field }))
    } else {
      setForm((prev) => ({ ...prev, [field]: value }))
    }
    if (error) setError('')
  }

  function handleScopeChange(scopeValues) {
    setForm((prev) => ({
      ...prev,
      companies: scopeValues.companies || [],
      branches: scopeValues.branches || [],
      departments: scopeValues.departments || [],
    }))
  }

  async function handleSave(options = { exitAfter: true }) {
    if (!form.name.trim()) {
      setError('A/c Name is required.')
      return
    }
    if (!form.group.trim()) {
      setError('A/c Group is required.')
      return
    }

    setIsSaving(true)
    setError('')
    setSuccess('')

    try {
      if (isEdit) {
        await updateAccountHead(id, form)
        setSuccess('Account Head updated successfully!')
      } else {
        await createAccountHead(form)
        setSuccess('Account Head created successfully!')
      }

      if (options.exitAfter) {
        setTimeout(() => {
          navigate('/masters/acheads/heads/list')
        }, 350)
      } else {
        // Save & Continue
        setForm((prev) => ({
          ...INITIAL_FORM,
          group: prev.group,
        }))
      }
    } catch (err) {
      setError(err?.message || 'Failed to save Account Head.')
    } finally {
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      availableGroups,
      isEdit,
      isLoading,
      isSaving,
      error,
      success,
    },
    actions: {
      patchForm,
      handleScopeChange,
      handleSave,
      handleCancel: () => navigate('/masters/acheads/heads/list'),
    },
  }
}
